import React, { useEffect, useRef, useState } from 'react'
import { Row } from '@lwt-helix/layout'
import { CustomInput } from '@lwt-helix/controls'
import { tableViewNames, tabModes } from '../constants/agentProductionConstants'
import { agentProductionTerms as apt, TimePeriods } from '../../constants'
import { getLabelTextWithDate, getTooltipText } from '../helpers/uiHelpers'
import ReactTooltip from 'react-tooltip';
import ReactDOMServer from 'react-dom/server';
import { useSearchStore, useSavedAgentsStore } from '../../store/store'
import { getMlsRestrictionSettings, getOptionsByMlsRestriction } from '../helpers/agentProductionHelpers'
import { tabs } from '../constants/suggestedAgents';
import { SectionColumn } from './common/SectionColumn'
import { colGroups, colProps, dataTestId as dataId, dataTestId } from '../../constants/metricsColSettings'
import PropTypes from 'prop-types';

const AgentsColumns = ({ defaultColumns, setParentColumnIds, tabMode, inputIdPrefix, customSettings, skipColumnIdsPersistence }) => {
    const ref = useRef();

    const {
        selectedModule,
        selectedMls,
        setPMSelectedColumnIds,
    } = useSearchStore(state => ({
        selectedModule: state.selectedModule,
        selectedMls: state[state.selectedModule]?.selectedMls,
        setPMSelectedColumnIds: state.setPMSelectedColumnIds,
    }))
    
    const {
        selectedList,
    } = useSavedAgentsStore(state => ({
        selectedList: state.selectedListByModule[selectedModule],
    }));

    const restrictedMlsSettings = getMlsRestrictionSettings(selectedMls?.mlsId || selectedList?.mlsId);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    const renderMetricsTooltip = data => {
        const metricDescription = JSON.parse(data);
        return metricDescription?.metricDescription && ReactDOMServer.renderToString(
            <div className="text-left">
                <div className="w-100">{metricDescription.metricDescription}</div>
            </div>
        );
    };

    const [selectedColumnIds, setSelectedColumnIds] = useState(
        defaultColumns.flatMap((c) => (c.isDisplayed && !c.parentColumnId ? c.id : []))
    );

    const handleItemClick = (e) => {
        const item = e.target;
        const clickedColumnId = item.value;

        const revisedSelectedItems = item.checked
            ? [...selectedColumnIds, clickedColumnId]
            : selectedColumnIds.filter(col => col !== clickedColumnId);

        !skipColumnIdsPersistence && setPMSelectedColumnIds(revisedSelectedItems, tabMode, customSettings?.isPeriodLast12Months);
        setSelectedColumnIds(revisedSelectedItems);
        setParentColumnIds(revisedSelectedItems);
    };

    const writeInput = (column, index) => {
        const foundColumn = selectedColumnIds.find(id => id === column.id);
        const elementId = `${inputIdPrefix}${column.id}`;
        const label = getLabelTextWithDate(column);
        const tooltipProps = {
            'data-for': 'metrics-selector-tooltip',
            'data-tip': JSON.stringify({
                metricDescription: getTooltipText(column),
            })
        };
        return <React.Fragment key={column.id}>
            <div {...tooltipProps} ref={ref}>
                <CustomInput
                        dataLwtId={elementId}
                        id={elementId}
                        value={column.id}
                        defaultChecked={foundColumn}
                        disabled={false}
                        type="checkbox"
                        label={label}
                        onClick={handleItemClick} />
            </div>
            <ReactTooltip
                id="metrics-selector-tooltip"
                className="metrics-selector-tooltip"
                type="info"
                effect='solid'
                place='left'
                backgroundColor='black'
                getContent={renderMetricsTooltip}
                html={true}
            />
        </React.Fragment>;
    };
    const writeColumn = rows => rows.map((row, index) => writeInput(row, index));

    const groupsToWrite = [];
    const isNewProducers = customSettings?.currentTabId === tabs.newProducers.id;
    const isCoopAgents = customSettings?.currentTabId === tabs.coopAgents.id;
    const isIncDecProducers = customSettings?.currentTabId === tabs.incProducers.id
        || customSettings?.currentTabId === tabs.decProducers.id;
    if (tabMode === tabModes.OVERVIEW) {

        const {
            getOfficeOptions,
            currentTimePeriodOptions,
            recentCoopTransactionOptions,
            getAllMlsOptions,
        } = colGroups[tabMode];

        const officeOptions = getOfficeOptions(isCoopAgents);
        const allMlsOptions = getAllMlsOptions(isIncDecProducers);

        const {
            allMlsColProps,
            newProducerColProps,
            coopAgentsColProps,
            defaultOfficeInfoColProps,
            defaultCurrentPeriodColProps
        } = colProps[tabMode];

        const officeInfoColProps = isNewProducers ? newProducerColProps : defaultOfficeInfoColProps;
        const currentPeriodColProps = isNewProducers ? newProducerColProps : defaultCurrentPeriodColProps;

        groupsToWrite.push(
            <SectionColumn colProps={officeInfoColProps} dataTestId={dataId.officeInfo} title={apt.officeInformation}>
              {writeColumn(officeOptions)}
            </SectionColumn>
        );
        isCoopAgents && groupsToWrite.push(
            <SectionColumn colProps={coopAgentsColProps} dataTestId={dataId.recentCoopTransaction} title={apt.recentCoopTransactions}>
                {writeColumn(recentCoopTransactionOptions)}
            </SectionColumn>
        );
        !isCoopAgents && groupsToWrite.push(
            <SectionColumn colProps={currentPeriodColProps} dataTestId={dataId.currentPeriod} title={apt.currentTimePeriodSearch}>
                {writeColumn(currentTimePeriodOptions)}
            </SectionColumn>
        );
        !isNewProducers && groupsToWrite.push(
            <SectionColumn colProps={allMlsColProps} dataTestId={dataId.last12Months} title={apt.allMlsTransactionsLast12Months}>
                {writeColumn(allMlsOptions)}
            </SectionColumn>
        );
    }

    if (tabMode === tabModes.CONTACT_INFORMATION) {

        const {
            officeOptions,
            contactInformationOptions,
            currentSearchOptions,
            allMlsOptions,
            growthOptions,
        } = colGroups[tabMode];

        const {
            newProducerColProps,
            defaultOfficeInfoColProps,
            defaultContactInfoColProps,
            defaultCurrentSearchColProps
        } = colProps[tabMode];

        const officeInfoColProps = isNewProducers ? newProducerColProps : defaultOfficeInfoColProps;
        const contactInfoColProps = isNewProducers ? newProducerColProps : defaultContactInfoColProps;
        const currentSearchColProps = isNewProducers ? newProducerColProps : defaultCurrentSearchColProps;

        groupsToWrite.push(
            <SectionColumn colProps={officeInfoColProps} dataTestId={dataId.officeInfo} title={apt.officeInformation}>
              {writeColumn(officeOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={contactInfoColProps} dataTestId={dataId.officeInfo} title={apt.contactInformation}>
                {writeColumn(getOptionsByMlsRestriction(contactInformationOptions, restrictedMlsSettings))}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={currentSearchColProps} dataTestId={dataId.officeInfo} title={apt.currentSearch}>
                {writeColumn(currentSearchOptions)}
            </SectionColumn>
        );
        !isNewProducers && groupsToWrite.push(
            <SectionColumn colProps={defaultCurrentSearchColProps} dataTestId={dataId.officeInfo} title={apt.allMlsTransactionsLast12Months}>
                {writeColumn(allMlsOptions)}
            </SectionColumn>
        );
        !isNewProducers && groupsToWrite.push(
            <SectionColumn colProps={defaultCurrentSearchColProps} dataTestId={dataId.officeInfo} title={apt.growth}>
                {writeColumn(growthOptions)}
            </SectionColumn>
        );
    }

    if (tabMode === tabModes.GROWTH_ANALYSIS) {

        let officeOptions = [];
        let unitOptions = [];
        let volumeOptions = [];

        const { defaultColProps } = colProps[tabModes.GROWTH_ANALYSIS];

        const sugAgentsTableViewByPeriod = {
            [apt.last12Months]: tableViewNames.growthAnalysisLtm,
            [apt.yearToDate]: tableViewNames.growthAnalysisYtd,
        }[customSettings?.growthAnalysisPeriod];

        const incDecTableViewByPeriod = {
            [TimePeriods[apt.last90Days]]: tableViewNames.growthAnalysisL90,
            [TimePeriods[apt.last12Months]]: tableViewNames.incDecGrowthAnalysisLtm,
            [TimePeriods[apt.yearToDate]]: tableViewNames.incDecGrowthAnalysisYtd,
        }[customSettings?.growthAnalysisPeriod]; 

        const defaultTableView = customSettings?.isPeriodLast12Months
                ? tableViewNames.growthAnalysisLtm
                : tableViewNames.growthAnalysisYtd;

        const growthAnalysisTableView = customSettings?.growthAnalysisPeriod ?
            isIncDecProducers ? incDecTableViewByPeriod : sugAgentsTableViewByPeriod : defaultTableView;

        const {
            getOfficeOptions,
            getUnitOptions,
            getVolumeOptions,
        } = colGroups[growthAnalysisTableView];

        officeOptions = getOfficeOptions();
        unitOptions = getUnitOptions(isIncDecProducers);
        volumeOptions = getVolumeOptions(isIncDecProducers);

        // Values for Col 'md' props target individual columns. They are overridden in the CSS to tighten space b/w columns
        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataId.officeInfo} title={apt.officeInformation}>
                {writeColumn(officeOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataId.units} title={apt.numberUnits}>
                {writeColumn(unitOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataId.volume} title={apt.dollarVolume}>
                {writeColumn(volumeOptions)}
            </SectionColumn>
        );
    }

    if (tabMode === tabModes.LISTING_PROFICIENCY) {

        const {
            officeOptions,
            totalSellOptions,
            totalListOptions,
            noPriceChangeOptions,
            priceChangeOptions,
        } = colGroups[tabMode];

        const {
            defaultColProps,
            officeInfoColProps,
            noPriceChangeColProps,
            priceChangeColProps
        } = colProps[tabModes.LISTING_PROFICIENCY];

        groupsToWrite.push(
            <SectionColumn colProps={officeInfoColProps} dataTestId={dataId.officeInfo} title={apt.officeInformation}>
                {writeColumn(officeOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataId.totalSell} title={apt.totalSell}>
                {writeColumn(totalSellOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataId.totalList} title={apt.totalList}>
                {writeColumn(totalListOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={noPriceChangeColProps} dataTestId={dataId.noPriceChange} title={apt.noPriceChangeListings}>
                {writeColumn(noPriceChangeOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={priceChangeColProps} dataTestId={dataId.priceChange} title={apt.priceChangeListings}>
                {writeColumn(priceChangeOptions)}
            </SectionColumn>
        );
    }

    if (tabMode === tabModes.ACTIVITY) {
        const {
            currentTimeOptions,
            allMlsTransactions
        } = colGroups[tabMode];
        const {
            defaultColProps
        } = colProps[tabModes.ACTIVITY]

        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataTestId.activity} title={apt.currentTimePeriodSearch}>
                {writeColumn(currentTimeOptions)}
            </SectionColumn>
        );
        groupsToWrite.push(
            <SectionColumn colProps={defaultColProps} dataTestId={dataTestId.activity} title={apt.allMlsTransactionsLast12Months}>
                {writeColumn(allMlsTransactions)}
            </SectionColumn>
        );
    }
    return (
        <>
            <Row>
                {groupsToWrite.map((group, index) => (
                    <React.Fragment key={index}>{group}</React.Fragment>
                ))}
            </Row>
        </>
    )
}

AgentsColumns.propTypes = {
    customSettings: PropTypes.object,
    defaultColumns: PropTypes.array.isRequired,
    inputIdPrefix: PropTypes.string.isRequired,
    setParentColumnIds: PropTypes.func.isRequired,
    tabMode: PropTypes.string.isRequired,
    skipColumnIdsPersistence: PropTypes.bool,
};

export default AgentsColumns;
